import { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Get the current user ID from Redux store or sessionStorage
 * @returns {string|null} The user ID or null if not found
 */
const getUserId = (reduxUserId) => {
  // First try Redux store
  if (reduxUserId) {
    return String(reduxUserId);
  }
  // Fallback to sessionStorage
  const sessionUserId = sessionStorage.getItem("user_id");
  if (sessionUserId) {
    return sessionUserId;
  }
  // Try localStorage as last resort
  const localUserId = localStorage.getItem("user_id");
  if (localUserId) {
    return localUserId;
  }
  return null;
};

/**
 * Custom hook to manage persistent column state for AgGridReact tables
 * Column state is saved per-user to ensure each user has their own customizations
 * @param {string} tableId - Unique identifier for the table to store state
 * @returns {object} Object containing column state handlers
 */
const useColumnState = (tableId) => {
  const gridApiRef = useRef(null);
  const isRestoringRef = useRef(false);
  const [isColumnStateReady, setIsColumnStateReady] = useState(!tableId); // If no tableId, start as ready
  const [savedColumnState, setSavedColumnState] = useState(null);

  // Get user ID from Redux store
  const reduxUserId = useSelector(
    (state) => state.profile?.user_id || state.profile?.id
  );

  // Generate user-specific storage key
  const stateKey = useMemo(() => {
    const userId = getUserId(reduxUserId);
    if (!tableId) return null;
    // Include userId in the key so each user has their own column state
    return userId
      ? `ag-grid-column-state-${userId}-${tableId}`
      : `ag-grid-column-state-${tableId}`; // Fallback for unauthenticated state
  }, [tableId, reduxUserId]);

  // Load saved state on initialization and when stateKey changes (e.g., after login)
  useEffect(() => {
    if (stateKey) {
      const saved = localStorage.getItem(stateKey);
      // Check for valid JSON string (not "undefined", "null", or empty)
      if (saved && saved !== "undefined" && saved !== "null" && saved.trim()) {
        try {
          const parsedState = JSON.parse(saved);
          setSavedColumnState(parsedState);

          // If grid is already ready and we just got the user ID (e.g., after login),
          // immediately restore the state
          if (
            gridApiRef.current &&
            parsedState &&
            Array.isArray(parsedState) &&
            parsedState.length > 0
          ) {
            // Small delay to ensure grid is fully ready
            setTimeout(() => {
              try {
                if (gridApiRef.current && !isRestoringRef.current) {
                  isRestoringRef.current = true;
                  gridApiRef.current.applyColumnState({
                    state: parsedState,
                    applyOrder: true,
                  });
                  setTimeout(() => {
                    isRestoringRef.current = false;
                  }, 100);
                }
              } catch (error) {
                console.warn(
                  `Failed to restore column state after stateKey change:`,
                  error
                );
                isRestoringRef.current = false;
              }
            }, 100);
          }
        } catch (error) {
          console.warn(
            `❌ Failed to parse saved column state for ${stateKey}:`,
            error
          );
          // Clear invalid state
          localStorage.removeItem(stateKey);
        }
      } else {
        // No saved state found - clear the state
        setSavedColumnState(null);
      }
    }
  }, [stateKey]);

  // Save column state to localStorage (per-user)
  const saveColumnState = useCallback(() => {
    if (gridApiRef.current && !isRestoringRef.current && stateKey) {
      try {
        const columnState = gridApiRef.current.getColumnState();
        localStorage.setItem(stateKey, JSON.stringify(columnState));
      } catch (error) {
        // Silent fail for column state save
      }
    }
  }, [stateKey]);

  // Restore column state from localStorage (per-user)
  const restoreColumnState = useCallback(() => {
    if (gridApiRef.current && stateKey) {
      const savedState = localStorage.getItem(stateKey);

      // Check for valid JSON string (not "undefined", "null", or empty)
      if (
        savedState &&
        savedState !== "undefined" &&
        savedState !== "null" &&
        savedState.trim()
      ) {
        try {
          isRestoringRef.current = true;
          const columnState = JSON.parse(savedState);

          // Apply column state immediately without animation to prevent flash
          gridApiRef.current.applyColumnState({
            state: columnState,
            applyOrder: true,
          });

          //   console.log(`✅ Column state restored for ${stateKey}:`, columnState);
        } catch (error) {
          console.warn(
            `❌ Failed to restore column state for ${stateKey}:`,
            error
          );
          // Clear invalid state
          localStorage.removeItem(stateKey);
        } finally {
          // Reset flag and mark as ready
          setTimeout(() => {
            isRestoringRef.current = false;
            setIsColumnStateReady(true);
          }, 100);
        }
      } else {
        // console.log(`ℹ️ No saved column state found for ${stateKey}`);
        setTimeout(() => {
          setIsColumnStateReady(true);
        }, 0);
      }
    }
  }, [stateKey]);

  // Handle grid ready event
  // CRITICAL: Read directly from localStorage to avoid stale closure issues
  // The savedColumnState might not be set yet when this callback fires
  const onGridReady = useCallback(
    (params) => {
      gridApiRef.current = params.api;

      // If no stateKey (column state disabled), mark as ready immediately
      if (!stateKey) {
        setTimeout(() => {
          setIsColumnStateReady(true);
        }, 0);
        return;
      }

      // Read directly from localStorage to avoid race condition
      // where savedColumnState hasn't been set by useEffect yet
      let columnStateToApply = null;
      try {
        const saved = localStorage.getItem(stateKey);
        if (saved && saved !== "undefined" && saved !== "null" && saved.trim()) {
          columnStateToApply = JSON.parse(saved);
        }
      } catch (error) {
        console.warn(`Failed to read column state from localStorage:`, error);
      }

      // If we have saved column state, apply it immediately before any rendering
      if (columnStateToApply && Array.isArray(columnStateToApply) && columnStateToApply.length > 0) {
        try {
          isRestoringRef.current = true;

          // Apply column state synchronously to prevent flash
          // NOTE: Don't use defaultState as it would reset sort for columns not in saved state
          params.api.applyColumnState({
            state: columnStateToApply,
            applyOrder: true,
          });

          // Restore filter state after column state is applied
          setTimeout(() => {
            restoreFilterState();
          }, 50);

          // Use setTimeout to avoid flushSync warning during lifecycle
          setTimeout(() => {
            setIsColumnStateReady(true);
          }, 0);
        } catch (error) {
          console.warn(
            `❌ Failed to apply column state on grid ready for ${stateKey}:`,
            error
          );
          setTimeout(() => {
            setIsColumnStateReady(true);
          }, 0);
        } finally {
          // Reset flag after a short delay
          setTimeout(() => {
            isRestoringRef.current = false;
          }, 100);
        }
      } else {
        // Still restore filter state even if no column state
        setTimeout(() => {
          restoreFilterState();
        }, 50);
        setTimeout(() => {
          setIsColumnStateReady(true);
        }, 0);
      }
    },
    [stateKey]
  );

  // Handle first data rendered event as backup
  const onFirstDataRendered = useCallback(
    (params) => {
      //   console.log(`📊 First data rendered for ${stateKey}`);

      // If no stateKey, ensure we're ready
      if (!stateKey) {
        setTimeout(() => {
          setIsColumnStateReady(true);
        }, 0);
        return;
      }

      // Only restore if not already done in onGridReady
      if (!isColumnStateReady) {
        setTimeout(() => {
          restoreColumnState();
        }, 50);
      }
    },
    [restoreColumnState, stateKey, isColumnStateReady]
  );

  // Save filter state to localStorage
  const saveFilterState = useCallback(() => {
    if (gridApiRef.current && !isRestoringRef.current && stateKey) {
      try {
        const filterModel = gridApiRef.current.getFilterModel();
        const filterKey = `${stateKey}-filters`;
        localStorage.setItem(filterKey, JSON.stringify(filterModel));
      } catch (error) {
        console.warn(`❌ Failed to save filter state for ${stateKey}:`, error);
      }
    }
  }, [stateKey]);

  // Restore filter state from localStorage
  const restoreFilterState = useCallback(() => {
    if (gridApiRef.current && stateKey) {
      try {
        const filterKey = `${stateKey}-filters`;
        const savedFilters = localStorage.getItem(filterKey);
        if (
          savedFilters &&
          savedFilters !== "undefined" &&
          savedFilters !== "null" &&
          savedFilters.trim()
        ) {
          const filterModel = JSON.parse(savedFilters);
          gridApiRef.current.setFilterModel(filterModel);
        }
      } catch (error) {
        console.warn(
          `❌ Failed to restore filter state for ${stateKey}:`,
          error
        );
      }
    }
  }, [stateKey]);

  // Debounce refs for column and filter saves
  const columnSaveTimeoutRef = useRef(null);
  const filterSaveTimeoutRef = useRef(null);

  // Handle column events (moved, resized, etc.)
  // FIXED: Save immediately on sort changes, debounce for resize/move to prevent excessive writes
  // Also clear any pending debounce before starting a new one
  const onColumnEvent = useCallback((event) => {
    if (!isRestoringRef.current && stateKey && gridApiRef.current) {
      // Clear any pending save
      if (columnSaveTimeoutRef.current) {
        clearTimeout(columnSaveTimeoutRef.current);
      }

      // For sort changes, save immediately to prevent loss when navigating
      if (event?.type === 'sortChanged') {
        saveColumnState();
      } else {
        // For other events (resize, move), use a short debounce
        columnSaveTimeoutRef.current = setTimeout(() => {
          saveColumnState();
        }, 100);
      }
    }
  }, [saveColumnState, stateKey]);

  // Handle filter changed event
  // Save immediately to prevent loss when navigating
  const onFilterChanged = useCallback(() => {
    if (!isRestoringRef.current && stateKey && gridApiRef.current) {
      // Clear any pending save
      if (filterSaveTimeoutRef.current) {
        clearTimeout(filterSaveTimeoutRef.current);
      }
      // Save immediately for filter changes
      saveFilterState();
    }
  }, [saveFilterState, stateKey]);

  // CRITICAL: Save state immediately on unmount to prevent data loss
  // Also clear any pending debounce timeouts
  useEffect(() => {
    // Capture refs in closure for cleanup
    const columnTimeoutRef = columnSaveTimeoutRef;
    const filterTimeoutRef = filterSaveTimeoutRef;
    
    // Also save on window blur/beforeunload to catch navigation
    const saveStateImmediately = () => {
      if (gridApiRef.current && stateKey) {
        try {
          const columnState = gridApiRef.current.getColumnState();
          if (columnState && columnState.length > 0) {
            localStorage.setItem(stateKey, JSON.stringify(columnState));
          }
          const filterModel = gridApiRef.current.getFilterModel();
          if (filterModel && Object.keys(filterModel).length > 0) {
            localStorage.setItem(
              `${stateKey}-filters`,
              JSON.stringify(filterModel)
            );
          }
        } catch (error) {
          // Silent fail
        }
      }
    };
    
    // Save state when window loses focus (user might be clicking a button)
    window.addEventListener('blur', saveStateImmediately);
    // Save state before page unload
    window.addEventListener('beforeunload', saveStateImmediately);
    
    return () => {
      // Remove listeners
      window.removeEventListener('blur', saveStateImmediately);
      window.removeEventListener('beforeunload', saveStateImmediately);
      
      // Clear any pending timeouts
      if (columnTimeoutRef.current) {
        clearTimeout(columnTimeoutRef.current);
      }
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
      
      // On unmount, save state immediately (no debounce)
      saveStateImmediately();
    };
  }, [stateKey]);

  return {
    gridApiRef,
    onGridReady,
    onFirstDataRendered,
    onColumnEvent,
    onFilterChanged,
    saveColumnState,
    restoreColumnState,
    saveFilterState,
    restoreFilterState,
    isColumnStateReady,
    savedColumnState, // Expose saved state for pre-configuration
  };
};

export default useColumnState;
